import {HealthDataType} from '@common/models'
import {useEffect, useState} from 'react'

const HEALTH_DATA_TYPES_STORAGE_KEY = 'selectedHealthDataTypes'
const KNOWN_HEALTH_DATA_TYPES_STORAGE_KEY = 'knownHealthDataTypes'

const healthDataTypesOrder = [
  HealthDataType.BLOOD_PRESSURE,
  HealthDataType.GLUCOSE_MMOL_L,
  HealthDataType.RESTING_HEART_RATE,
  HealthDataType.HEART_RATE_VARIABILITY_MS,
  HealthDataType.HEART_AGE_YEARS,
  HealthDataType.VO2_MAX,
  HealthDataType.OXYGEN_SATURATION_PERCENT,
  HealthDataType.RESPIRATION_RATE,
  HealthDataType.PEAK_FLOW,
  HealthDataType.WEIGHT_KG,
  HealthDataType.HEIGHT_CM,
  HealthDataType.WAIST_CIRCUMFERENCE_CM,
  HealthDataType.BMI,
  HealthDataType.BODY_FAT_PERCENTAGE,
  HealthDataType.TOTAL_CHOLESTEROL_MMOL_L,
  HealthDataType.HDL_MMOL_L,
  HealthDataType.LDL_MMOL_L,
  HealthDataType.TRIGLYCERIDES_MMOL_L,
  HealthDataType.CHOLESTEROL_HDL_RATIO,
  HealthDataType.STRESS_INDEX,
  HealthDataType.MOOD_SCORE,
  HealthDataType.QUALITY_SLEEP_SECONDS,
  HealthDataType.SLEEP_SCORE,
  HealthDataType.ACTIVE_MINUTES,
  HealthDataType.STEPS,
  HealthDataType.ACTIVELY_BURNED_CALORIES,
  HealthDataType.MOVEMENT_SCORE,
  HealthDataType.DIET_SCORE,
  HealthDataType.WATER_CONSUMPTION_ML,
  HealthDataType.ALCOHOL_SCORE,
  HealthDataType.TOBACCO_SCORE,
]

const healthDataTypesOrderMap = healthDataTypesOrder.reduce(
  (orderMap, healthDataType) => ({
    ...orderMap,
    [healthDataType]: healthDataTypesOrder.indexOf(healthDataType),
  }),
  {} as Partial<Record<HealthDataType, number>>,
)

const defautHiddenHealthDataTypes = [
  HealthDataType.HEIGHT_CM,
  HealthDataType.BMI,
]

const sortHealthDataTypes = (healthDataTypes: HealthDataType[]) =>
  healthDataTypes.slice().sort((a, b) => {
    const orderA = healthDataTypesOrderMap[a] ?? Number.MAX_SAFE_INTEGER
    const orderB = healthDataTypesOrderMap[b] ?? Number.MAX_SAFE_INTEGER
    return orderA - orderB
  })

const defaultHealthDataTypes = sortHealthDataTypes(
  Object.values(HealthDataType).filter(
    (healthDataType) => !defautHiddenHealthDataTypes.includes(healthDataType),
  ),
)

const persistKnownHealthDataTypes = () => {
  window.localStorage.setItem(
    KNOWN_HEALTH_DATA_TYPES_STORAGE_KEY,
    JSON.stringify(Object.values(HealthDataType)),
  )
}

const typesAddedAfterPersistedSelection = [
  HealthDataType.TOTAL_CHOLESTEROL_MMOL_L,
  HealthDataType.HDL_MMOL_L,
  HealthDataType.LDL_MMOL_L,
  HealthDataType.TRIGLYCERIDES_MMOL_L,
]

const getNewlyIntroducedDefaultTypes = (stored: HealthDataType[]) => {
  const storedSet = new Set(stored)
  const defaultSet = new Set(defaultHealthDataTypes)
  const knownRaw = window.localStorage.getItem(
    KNOWN_HEALTH_DATA_TYPES_STORAGE_KEY,
  )

  if (knownRaw) {
    const knownSet = new Set(JSON.parse(knownRaw) as HealthDataType[])
    return Object.values(HealthDataType).filter(
      (type) =>
        defaultSet.has(type) && !storedSet.has(type) && !knownSet.has(type),
    )
  }

  return typesAddedAfterPersistedSelection.filter(
    (type) => defaultSet.has(type) && !storedSet.has(type),
  )
}

export const useSelectedHealthDataTypes = () => {
  const [selectedHealthDataTypes, setSelectedHealthDataTypes] = useState(
    defaultHealthDataTypes,
  )

  const selectHealthDataTypes = (healthDataTypes: HealthDataType[]) => {
    setSelectedHealthDataTypes(sortHealthDataTypes(healthDataTypes))

    window.localStorage.setItem(
      HEALTH_DATA_TYPES_STORAGE_KEY,
      JSON.stringify(healthDataTypes),
    )
    persistKnownHealthDataTypes()
  }

  useEffect(() => {
    const storedHealthDataTypes = window.localStorage.getItem(
      HEALTH_DATA_TYPES_STORAGE_KEY,
    )
    if (!storedHealthDataTypes) {
      persistKnownHealthDataTypes()
      return
    }

    const stored: HealthDataType[] = JSON.parse(storedHealthDataTypes)
    // Persisted "Data shown" lists omit types added in later releases, so a
    // refresh would keep hiding them. Merge types the user could not have
    // unchecked yet; do not restore types they already hid.
    const typesToAdd = getNewlyIntroducedDefaultTypes(stored)
    const nextSelected = sortHealthDataTypes(
      typesToAdd.length > 0 ? [...stored, ...typesToAdd] : stored,
    )

    if (typesToAdd.length > 0) {
      window.localStorage.setItem(
        HEALTH_DATA_TYPES_STORAGE_KEY,
        JSON.stringify(nextSelected),
      )
    }
    persistKnownHealthDataTypes()
    setSelectedHealthDataTypes(nextSelected)
  }, [])

  return {selectedHealthDataTypes, selectHealthDataTypes}
}
